<template>
  <v-stepper-step :step="currentCategory.orderIndex"
                  :complete="currentStep > currentCategory.orderIndex"
                  :class="[$style.step, StepStyle]"
                  @click="$emit('stepChange', currentCategory.orderIndex)">
    <div :class="$style.stepTitleContainer">
      <div v-if="itemSelectedInCategory(categoryKey)"
           :class="$style.stepCheckmark">
        <svg v-svg
             :class="$style.checkmarkIcon"
             symbol="checkmark-icon"/>
      </div>
      <div v-else
           :class="$style.stepCheckmarkNotValid">
        <svg v-svg
             :class="$style.checkmarkIcon"
             symbol="checkmark-icon"/>
      </div>
      <h4 :class="$style.stepTitle">{{ currentCategory.name }}</h4>
    </div>
  </v-stepper-step>
</template>
<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Getter } from 'vuex-class';

/**
 * Renders current category tittle on the configurator header.
 *
 * Props:
 * @property {any} categoryKey - Key for category in store.
 * @property {number} currentStep - Current step of the configurator.
 */
@Component({
  name:  'ConfiguratorStepTitle',
  props: {
    categoryKey: {},
    currentStep: {},
    stepsAmount: 0,
  },
})
export default class ConfiguratorStepTitle extends Vue {
  /**
   * Retrieves categories from the store.
   */
  @Getter categoryByKey;
  /**
   * Check the selected element in current category.
   */
  @Getter itemSelectedInCategory;

  /**
   * Retrieves key for product configurator.
   */
  @Getter currentProductKey;

  @Getter isBurgerBossing;

  /**
   * Retrieves selected category from state.
   * @returns {{
   * name: string,
   * orderIndex: number,
   * widget: string,
   * subProduct: any,
   * items: [any],
   * }}
   */
  get currentCategory() {
    const catByKey = this.categoryByKey(this.categoryKey);

    // TODO move this behavior outside of this component, this is not the responsibility of the configurator component
    const isBossBundleBeingConfigured =
      this.isBurgerBossing && this.categoryKey > 70 && this.categoryKey < 99;

    if (isBossBundleBeingConfigured) {
      // We should add 5 to bundle items order indexes because they go after the main configurator that has 6 steps
      return {
        ...catByKey,
        orderIndex: catByKey.orderIndex + 5,
      };
    }

    return catByKey;
  }

  /**
   * Get style of the step.
   * Hide all steps but the current, the previous and the next on small screens.
   * Display only three steps on small screens.
   * If the current step is the first one - display it and two next steps
   * If the current step is the last one - display it and two previous steps
   */
  get StepStyle() {
    switch (this.currentCategory.orderIndex) {
      case (this.currentStep):
        return [ this.$style.current, this.$style.checked ];

      case (this.currentStep - 1):
        return [ this.$style.prev, this.$style.checked ];

      case (this.currentStep - 2):
        if (this.currentStep === this.stepsAmount) {
          return [ this.$style.prev, this.$style.checked ];
        }
        break;

      case (this.currentStep + 1):
        return this.$style.next;

      case (this.currentStep + 2):
        if (this.currentStep === 1) {
          return this.$style.next;
        }
        break;

      default:
        break;
    }

    if (this.currentCategory.orderIndex < this.currentStep) {
      return this.$style.checked;
    }

    return false;
  }
}
</script>

<style lang="scss" module>
  @import "@/style/variables.scss";
  $step-checkmark-color: #B3B3B3;
  $step-shadow-color: #E1E1E1;
  $step-checked: #DEDEDE;

  .step {
    background-color: $light-choice-bg;
    width: 14.3%;
    padding-left: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    position: relative;
    transition: all 0.3s ease-in-out;
    cursor: pointer;
    &::before {
      content: '';
      position: absolute;
      width: 23px;
      height: 50%;
      top: 0;
      left: calc(100% - 16px);
      z-index: 3;
      background-color: $light-choice-bg;
      box-shadow: 1px 0 0 0 $white;
      transform: skew(30deg);
      transition: all 0.3s ease-in-out;
    }
    &::after {
      content: '';
      position: absolute;
      width: 23px;
      height: 50%;
      top: auto;
      bottom: 1px;
      left: calc(100% - 16px);
      z-index: 3;
      background-color: $light-choice-bg;
      box-shadow: 1px 0 0 0 $white;
      transform: skew(-30deg);
      transition: all 0.3s ease-in-out;
    }
    &:first-child {
      padding-left: 0;
    }
  }

  .stepTitleContainer {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    .stepTitle {
      color: $gray-transparent;
      font-family: 'Gotham', sans-serif;
      font-size: 17px;
      line-height: 17px;
      font-weight: 700;
      margin: 0;
      text-align: left;
      text-transform: uppercase;
      display: block;
      max-width: calc(100% - 62px);
      z-index: 4;
      transition: all 0.3s ease-in-out;
    }
  }

  .stepCheckmark,
  .stepCheckmarkNotValid {
    display: inline-block;
    width: 24px;
    height: 24px;
    margin-right: 8px;
    background-color: $step-checkmark-color;
    border-radius: 50%;
    opacity: 0.5;
    transition: all 0.3s ease-in-out;
    .checkmarkIcon {
      width: 24px;
      height: 24px;
      fill: $checkmark-color;
      transition: all 0.3s ease-in-out;
    }
  }
  .stepCheckmark {
    background-color: $green-color;
  }
  .stepCheckmarkNotValid {
    background-color: $gray-color;
  }

  .step.checked {
    background-color: $white;
    .stepTitle {
      color: $black-slightly-transparent;
    }
    .stepCheckmark,
    .stepCheckmarkNotValid {
      opacity: 1;
    }
    &::before,
    &::after {
      background-color: $white;
      box-shadow: 1px 0 0 0 $step-shadow-color;
    }
    &:hover,
    &:focus {
      background-color: $step-checked;
      &::before,
      &::after {
        background-color: $step-checked;
      }
    }

  }

  .step.checked:active {
    background-color: $white;
    &::before,
    &::after {
      background-color: $white;
    }
  }

  .step > span {
    display: none;
  }

  .step + hr {
    display: none;
  }

  @media screen and (max-width: 1400px) {
    .step {
      width: 33.33%;
      padding-left: 30px;
      display: none;
      &.prev,
      &.current,
      &.next {
        display: flex;
      }
      &::after,
      &::before {
        width: 30px;
      }
    }
  }


  @media screen and (max-width: $kiosk-breakpoint-max) {
    .stepTitleContainer .stepTitle {
      font-size: 34px;
      line-height: 38px;
    }
    .stepCheckmark {
      width: 37px;
      height: 37px;
      .checkmarkIcon {
        width: 37px;
        height: 37px;
      }
    }
  }

  @media screen and (max-width: 1020px) {
    .stepTitleContainer .stepTitle {
      font-size: 17px;
      line-height: 17px;
    }
    .stepCheckmark {
      width: 24px;
      height: 24px;
      .checkmarkIcon {
        width: 24px;
        height: 24px;
      }
    }
  }

  @media screen and (max-width: 767px) {
    .stepTitleContainer .stepTitle {
      font-size: 14px;
      line-height: 16px;
      max-width: none;
    }
    .stepCheckmark {
      width: 16px;
      height: 16px;
      .checkmarkIcon {
        width: 16px;
        height: 16px;
      }
    }
    .step {
      padding: 0;
      padding-left: 20px;
      &::after,
      &::before {
        width: 23px;
      }
    }
  }

  @media screen and (max-width: 360px) {
    .stepTitleContainer .stepTitle {
      font-size: 12px;
      line-height: 16px;
    }
  }

</style>



// WEBPACK FOOTER //
// src/apps/configurator/components/ConfiguratorStepTitle.vue